import Konva from "konva";
import React, { ComponentProps } from "react";
import { Image, Layer, Transformer } from "react-konva";

import { Area } from "../types";

interface ImageLayerProps {
  image: HTMLImageElement;
  imageArea: Area;
  onImageAreaChange: (area: ImageLayerProps["imageArea"]) => void;
}

export const ImageLayer = (props: ImageLayerProps) => {
  const { image, imageArea, onImageAreaChange } = props;

  const imageRef = React.useRef<Konva.Image>(null);
  const trRef = React.useRef<Konva.Transformer>(null);

  React.useEffect(() => {
    if (image && imageRef.current && trRef.current) {
      trRef.current.nodes([imageRef.current]);
    }
  }, [image]);

  const handleTransform = () => {
    const node = imageRef.current;

    if (!node) return;

    const scaleX = Math.abs(node.scaleX());
    const scaleY = Math.abs(node.scaleY());

    // make sure the scale is 1, we never want to stretch the image
    node.scaleX(1);
    node.scaleY(1);

    onImageAreaChange({
      ...imageArea,
      x: node.x(),
      y: node.y(),
      width: Math.max(10, node.width() * scaleX),
      height: Math.max(10, node.height() * scaleY),
    });
  };

  const handleDragEnd = () => {
    onImageAreaChange({
      ...imageArea,
      x: imageRef.current?.x() || 0,
      y: imageRef.current?.y() || 0,
    });
  };

  const handleWheel: ComponentProps<typeof Image>["onWheel"] = (e) => {
    const { evt } = e;
    evt.preventDefault();

    const { deltaY } = evt;
    const direction = Math.sign(deltaY);
    const isZoomIn = direction > 0;

    const factor = isZoomIn ? 1.1 : 0.9;

    // Calculate the center point of the current image
    const centerX = imageArea.x + imageArea.width / 2;
    const centerY = imageArea.y + imageArea.height / 2;

    // Calculate new dimensions
    const newWidth = imageArea.width * factor;
    const newHeight = imageArea.height * factor;

    // Calculate new position to keep the center point the same
    const newX = centerX - newWidth / 2;
    const newY = centerY - newHeight / 2;

    onImageAreaChange({
      ...imageArea,
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
    });
  };

  return (
    <Layer>
      <Image
        ref={imageRef}
        image={image}
        {...imageArea}
        draggable
        onTransform={handleTransform}
        onDragEnd={handleDragEnd}
        onWheel={handleWheel}
      />
      <Transformer
        ref={trRef}
        rotateEnabled={false}
        enabledAnchors={[
          "top-left",
          "top-right",
          "bottom-left",
          "bottom-right",
        ]}
        boundBoxFunc={(oldBox, newBox) => {
          if (Math.abs(newBox.width) < 10 || Math.abs(newBox.height) < 10) {
            return oldBox;
          }
          return newBox;
        }}
      />
    </Layer>
  );
};
